import type { FC } from 'react'
import React, { useEffect, useState } from 'react'

import type { Board, Position, Tile } from '@logic/board'
import { checkIfPositionsMatch, copyBoard } from '@logic/board'
import type { Move, Piece } from '@logic/pieces'
import {
  checkIfSelectedPieceCanMoveHere,
  createId,
  detectGameOver,
  getTile,
  movesForPiece,
  oppositeColor,
  shouldPromotePawn,
} from '@logic/pieces'
import { isPawn } from '@logic/pieces/pawn'
import { BishopComponent } from '@models/Bishop'
import type { ModelProps } from '@models/index'
import { MeshWrapper } from '@models/index'
import { KingComponent } from '@models/King'
import { KnightComponent } from '@models/Knight'
import { PawnModel } from '@models/Pawn'
import { QueenComponent } from '@models/Queen'
import { RookComponent } from '@models/Rook'
import { TileComponent } from '@models/Tile'
import { useSpring } from '@react-spring/three'
import { OrbitControls } from '@react-three/drei'
import { useRef } from 'react'

import type { GameOver } from '@/App'
import { isKing } from '@/logic/pieces/king'
import { isRook } from '@/logic/pieces/rook'
import { useGameSettingsState } from '@/state/game'
import { useHistoryState } from '@/state/history'
import { isDev } from '@/utils/isDev'

type ThreeMouseEvent = {
  stopPropagation: () => void
}

export type MovingTo = {
  move: Move
  tile: Tile
}

export const BoardComponent: FC<{
  selected: Piece | null
  setSelected: (piece: Piece | null) => void
  board: Board
  setBoard: React.Dispatch<React.SetStateAction<Board>>
  moves: Move[]
  setGameOver: (gameOver: GameOver | null) => void
  setMoves: (moves: Move[]) => void
  isComputerThinking?: boolean
  gameOver?: GameOver | null
}> = ({
  selected,
  setSelected,
  board,
  setBoard,
  moves,
  setMoves,
  setGameOver,
  isComputerThinking = false,
  gameOver = null,
}) => {
  const [lastSelected, setLastSelected] = useState<Tile | null>(null)
  const [history, setHistory] = useHistoryState((state) => [
    state.history,
    state.addItem,
  ])
  const [turn, setTurn, gameStarted, movingTo, setMovingTo] =
    useGameSettingsState((state) => [
      state.turn,
      state.setTurn,
      state.gameStarted,
      state.movingTo,
      state.setMovingTo,
    ])

  const [redLightPosition, setRedLightPosition] = useState<Position>({
    x: 0,
    y: 0,
  })

  const orbitControlsRef = useRef<any>(null)

  // Set initial azimuth angle
  useEffect(() => {
    if (orbitControlsRef.current) {
      orbitControlsRef.current.setAzimuthalAngle(-6.4)
      orbitControlsRef.current.setPolarAngle(.9)

    }
  }, [])

  const selectThisPiece = (e: ThreeMouseEvent, tile: Tile | null) => {
    e.stopPropagation()
    
    // In single-player mode, player is always white, computer is black
    const isPlayersTurn = turn === 'white' || isDev
    
    if (!isPlayersTurn || !gameStarted || isComputerThinking || gameOver) {
      return
    }
    
    // Only allow selecting white pieces (player pieces)
    if (!tile?.piece || tile.piece.color !== 'white') {
      return
    }

    setMovingTo(null)
    setMoves(
      movesForPiece({ piece: tile.piece, board, propagateDetectCheck: true }),
    )
    setSelected(tile.piece)
    setLastSelected(tile)
    setRedLightPosition(tile.position)
  }

  const finishMovingPiece = (tile: Tile | null) => {
    if (!tile || !movingTo || gameOver) return
    const newHistoryItem = {
      board: copyBoard(board),
      to: movingTo.move.newPosition,
      from: movingTo.move.piece.position,
      steps: movingTo.move.steps,
      capture: movingTo.move.capture,
      type: movingTo.move.type,
      piece: movingTo.move.piece,
    }
    setHistory(newHistoryItem)
    setBoard((prev) => {
      const newBoard = copyBoard(prev)
      if (!movingTo.move.piece) return prev
      const selectedTile = getTile(newBoard, movingTo.move.piece.position)
      const tileToMoveTo = getTile(newBoard, tile.position)
      if (!selectedTile || !tileToMoveTo) return prev

      if (
        isPawn(selectedTile.piece) ||
        isKing(selectedTile.piece) ||
        isRook(selectedTile.piece)
      ) {
        selectedTile.piece = { ...selectedTile.piece, hasMoved: true }
      }
      if (isPawn(selectedTile.piece) && shouldPromotePawn({ tile })) {
        // Create a proper queen piece instead of just changing type
        const promotedQueen = {
          type: 'queen' as const,
          color: selectedTile.piece!.color,
          id: selectedTile.piece!.id + 1,
          getId: () => `queen-${selectedTile.piece!.color}-${selectedTile.piece!.id + 1}`,
          position: tile.position,
        }
        selectedTile.piece = promotedQueen
      }

      if (
        isPawn(selectedTile.piece) &&
        movingTo.move.type === `captureEnPassant`
      ) {
        const latestMove = history[history.length - 1]
        const enPassantTile = newBoard[latestMove.to.y][latestMove.to.x]
        enPassantTile.piece = null
      }

      if (movingTo.move.castling) {
        const rookTile =
          newBoard[movingTo.move.castling.rook.position.y][
            movingTo.move.castling.rook.position.x
          ]
        const rookTileToMoveTo =
          newBoard[movingTo.move.castling.rookNewPosition.y][
            movingTo.move.castling.rookNewPosition.x
          ]
        if (!isRook(rookTile.piece)) return prev

        rookTileToMoveTo.piece = {
          ...rookTile.piece,
          hasMoved: true,
          position: rookTileToMoveTo.position,
        }
        rookTile.piece = null
      }

      tileToMoveTo.piece = selectedTile.piece
        ? { ...selectedTile.piece, position: tile.position }
        : null
      selectedTile.piece = null
      return newBoard
    })

    setTurn()

    setMovingTo(null)
    setMoves([])
    setSelected(null)
    setLastSelected(null)
  }

  useEffect(() => {
    const gameOverType = detectGameOver(board, turn)
    if (gameOverType) {
      setGameOver({ type: gameOverType, winner: oppositeColor(turn) })
    }
  }, [board, turn])

  const startMovingPiece = (e: ThreeMouseEvent, tile: Tile, nextTile: Move) => {
    e.stopPropagation()
    if (gameOver) return
    // In single-player mode, directly apply the move
    const newMovingTo: MovingTo = {
      move: nextTile,
      tile: tile,
    }
    setMovingTo(newMovingTo)
  }

  const { intensity } = useSpring({
    intensity: selected ? 0.35 : 0,
  })

  return (
    <group position={[-3.5, -0.5, -3.5]}>
      <OrbitControls
        ref={orbitControlsRef}
        maxDistance={25}
        minDistance={7}
        enableZoom={true}
        enablePan={false}
      />
      <hemisphereLight intensity={0.7} color="#3a4a6d" groundColor="#10131a" />
      <pointLight
        position={[3.5, 10, 3.5]}
        intensity={0.7}
        color="#e0e6f8"
        castShadow
        shadow-mapSize={[2048, 2048]}
      />
      {board.map((row, i) => {
        return row.map((tile, j) => {
          const bg = `${(i + j) % 2 === 0 ? `white` : `black`}`
          const isSelected =
            tile.piece && selected?.getId() === tile.piece.getId()

          const canMoveHere = checkIfSelectedPieceCanMoveHere({
            tile,
            moves,
            selected,
          })

          const tileId = tile.piece?.getId()
          const pieceIsBeingReplaced =
            movingTo?.move.piece && tile.piece && movingTo?.move.capture
              ? tileId === createId(movingTo?.move.capture)
              : false
          const rookCastled = movingTo?.move.castling?.rook
          const isBeingCastled =
            rookCastled && createId(rookCastled) === tile.piece?.getId()

          const handleClick = (e: ThreeMouseEvent) => {
            if (movingTo) {
              return
            }

            const tileContainsOtherPlayersPiece =
              tile.piece && tile.piece?.color !== turn

            if (tileContainsOtherPlayersPiece && !canMoveHere && !isDev) {
              setSelected(null)
              return
            }

            if (canMoveHere) {
              startMovingPiece(e, tile, canMoveHere)
            } else {
              selectThisPiece(e, tile)
            }
          }

          const props: ModelProps = {
            position: [j, 0.5, i],
            scale: [0.15, 0.15, 0.15],
            color: tile.piece?.color || `white`,
            onClick: handleClick,
            isSelected: isSelected ? true : false,
            wasSelected: lastSelected
              ? lastSelected?.piece?.getId() === tile.piece?.getId()
              : false,
            canMoveHere: canMoveHere?.newPosition ?? null,
            movingTo:
              checkIfPositionsMatch(
                tile.position,
                movingTo?.move.piece?.position,
              ) && movingTo
                ? movingTo.move.steps
                : isBeingCastled
                ? movingTo.move.castling?.rookSteps ?? null
                : null,
            pieceIsBeingReplaced: pieceIsBeingReplaced ? true : false,
            finishMovingPiece: () =>
              isBeingCastled ? null : finishMovingPiece(movingTo?.tile ?? null),
          }



          const pieceId = tile.piece?.getId() ?? `empty-${j}-${i}`

          return (
            <group key={`${j}-${i}`}>
              <TileComponent
                color={bg}
                position={[j, 0.25, i]}
                onClick={handleClick}
                canMoveHere={canMoveHere?.newPosition ?? null}
              />
              <MeshWrapper key={pieceId} {...props}>
                {tile.piece?.type === `pawn` && <PawnModel />}
                {tile.piece?.type === `rook` && <RookComponent />}
                {tile.piece?.type === `knight` && <KnightComponent />}
                {tile.piece?.type === `bishop` && <BishopComponent />}
                {tile.piece?.type === `queen` && <QueenComponent />}
                {tile.piece?.type === `king` && <KingComponent />}
              </MeshWrapper>
            </group>
          )
        })
      })}
    </group>
  )
}